import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TableContainer,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  Table,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Select,
  MenuItem,
  Snackbar,
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  FilterList as FilterListIcon,
  OpenInNew as OpenInNewIcon, 
} from '@mui/icons-material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import api from '../api/api';
import ProgramarConsulta from './ProgramarConsulta';
import FiltrosCalendario, { initialFilters } from './FiltrosCalendario'; 
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';



dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('es');

const cleanDateString = (dateStr) => {
  // Elimina la 'Z' (indicador de UTC) para forzar la interpretación local de dayjs.
  if (typeof dateStr === 'string' && dateStr.endsWith('Z')) {
    return dateStr.slice(0, -1);
  }
  return dateStr;
};

const CitasCalendario = () => {
  console.log('Versión de CitasCalendario.js cargada', new Date().toISOString());
  const { id: idPaciente } = useParams();
  const navigate = useNavigate();
  const [citas, setCitas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCita, setSelectedCita] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [programarModalOpen, setProgramarModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [currentDateRange, setCurrentDateRange] = useState({ start: null, end: null });
  const [sucursales, setSucursales] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [newEstadoCita, setNewEstadoCita] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [filters, setFilters] = useState(initialFilters);
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const calendarRef = useRef(null);

  const estadosCita = [
    { value: 'PRG', label: 'Programada' },
    { value: 'APL', label: 'Aplicada' },
    { value: 'CAN', label: 'Cancelada' },
    { value: 'PEN', label: 'Pendiente' },
  ];

  const { selectedSucursal, selectedEstadoCita, selectedCategoria, nombreCliente, nombrePaciente } = filters;

  useEffect(() => {
    const fetchSucursales = async () => {
      try {
        const response = await api.get('/sucursales/');
        console.log('Sucursales recibidas:', response.data);
        setSucursales(response.data);
      } catch (err) {
        console.error('Error al obtener sucursales:', err);
        setError('No se pudieron cargar las sucursales.');
      }
    };

    const fetchCategorias = async () => {
      try {
        const response = await api.get('/citas/categorias');
        console.log('Categorías recibidas:', response.data);
        setCategorias(response.data);
      } catch (err) {
        console.error('Error al obtener categorías:', err);
        setError('No se pudieron cargar las categorías.');
      }
    };

    if (!idPaciente) {
      fetchSucursales();
      fetchCategorias();
    }
  }, [idPaciente]);

  const fetchCitas = useCallback(async (reset = false, fechaInicio = null, fechaFin = null, idSucursal = selectedSucursal, estadoCita = selectedEstadoCita, tabCategoria = selectedCategoria, cliente = nombreCliente, paciente = nombrePaciente) => {
    if (!hasMore && !reset) {
      console.log('No hay más citas para cargar en este rango de fechas.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const currentPage = reset ? 0 : page;
      const limit = 100;
      let endpoint = idPaciente
        ? `/citas/con-consultas-nombre/${idPaciente}?limit=${limit}&offset=${currentPage * limit}`
        : `/citas/${cliente || paciente ? 'search' : 'all'}?limit=${limit}&offset=${currentPage * limit}`;

      if (fechaInicio && fechaFin) {
        endpoint += `&fecha_inicio=${encodeURIComponent(fechaInicio)}&fecha_fin=${encodeURIComponent(fechaFin)}`;
      }
      if (idSucursal) {
        endpoint += `&id_sucursal=${idSucursal}`;
      }
      if (estadoCita) {
        endpoint += `&estado_cita=${estadoCita}`;
      }
      if (tabCategoria) {
        endpoint += `&tab_categoria=${tabCategoria}`;
      }
      if (cliente) {
        endpoint += `&nombre_cliente=${encodeURIComponent(cliente)}`;
      }
      if (paciente) {
        endpoint += `&nombre_paciente=${encodeURIComponent(paciente)}`;
      }

      console.log('Payload enviado:', {
        endpoint,
        idPaciente,
        page: currentPage,
        offset: currentPage * limit,
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
        id_sucursal: idSucursal,
        estado_cita: estadoCita,
        tab_categoria: tabCategoria,
        nombre_cliente: cliente,
        nombre_paciente: paciente
      });

      const response = await api.get(endpoint);
      console.log('Respuesta del servidor:', response.data);
      const citasData = Array.isArray(response.data) ? response.data : [];
      console.log('Citas recibidas:', citasData.length);

      const calendarEvents = citasData
        .map((cita, index) => {
          console.log('CAMPOS CITA COMPLETA:', JSON.stringify(cita, null, 2));
          console.log('Fecha cruda del backend:', cita.id_cita, cita.fecha_cita);
          let fechaCita = cita.fecha_cita;
          if (fechaCita && !fechaCita.endsWith('Z')) {
            fechaCita = fechaCita + '.000Z';
            console.warn(`Fecha sin .000Z, corregida a: ${fechaCita}`);
          }
          const startDate = fechaCita ? dayjs.utc(fechaCita) : null;
          if (!startDate || !startDate.isValid()) {
            console.warn(`Fecha inválida para cita ${cita.id_cita}: ${cita.fecha_cita}`);
            return null;
          }
          const duracionProgramada = cita.duracion || 30;
          const endDate = startDate.add(duracionProgramada, 'minute');
          const event = {
            id: cita.id_cita,
            //title: `${cita.nombre_paciente || 'N/A'} (${cita.nombre_cliente || 'N/A'}) - ${cita.descripcion || 'Sin descripción'}`,
            title: `${cita.nombre_paciente || 'N/A'} (${cita.nombre_cliente || 'N/A'}) - ${cita.descripcion || cita.razon_consulta || 'Sin descripción'}`,

            start: startDate.toDate(), // Objeto Date en UTC
            end: endDate.toDate(), // Objeto Date en UTC
            backgroundColor: getEventColor(cita.estado_cita || 'PEN'),
            extendedProps: {
              ...cita,
              duracion: duracionProgramada,
            },
          };
          console.log('Evento creado para FullCalendar:', {
            id: cita.id_cita,
            start: event.start.toISOString(),
            end: event.end.toISOString(),
            startISO: startDate.toISOString(),
            endISO: endDate.toISOString()
          });
          return event;
        })
        .filter(event => event !== null);

      setCitas(prev => {
        const newEvents = reset ? calendarEvents : [...prev, ...calendarEvents];
        return Array.from(new Set(newEvents.map(e => e.id))).map(id => newEvents.find(e => e.id === id));
      });

      setHasMore(citasData.length === limit);
      setPage(currentPage + 1);
    } catch (err) {
      console.error('Error al obtener citas:', err);
      setError(
        err.response?.status === 403
          ? 'No tienes permisos para ver estas citas.'
          : err.response?.status === 422
          ? 'Debe proporcionar al menos un nombre de cliente o paciente.'
          : 'No se pudieron cargar las citas. Intenta de nuevo.'
      );
      setSnackbarMessage(
        err.response?.status === 422
          ? 'Debe proporcionar al menos un nombre de cliente o paciente.'
          : 'Error al cargar las citas.'
      );
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  }, [idPaciente, page, hasMore, selectedSucursal, selectedEstadoCita, selectedCategoria, nombreCliente, nombrePaciente]);

  const getEventColor = (estado) => {
    switch (estado) {
      case 'PRG': return '#1976d2';
      case 'APL': return '#388e3c';
      case 'CAN': return '#d32f2f';
      case 'PEN': return '#f57c00';
      default: return '#757575';
    }
  };

  const handleDatesSet = useCallback((dateInfo) => {
    const startDate = dayjs(dateInfo.start);
    const endDate = dayjs(dateInfo.end);
    const fechaInicio = startDate.startOf('day').format('YYYY-MM-DD HH:mm:ss');
    const fechaFin = endDate.endOf('day').format('YYYY-MM-DD HH:mm:ss');
    console.log('Cambiando rango de fechas:', { fecha_inicio: fechaInicio, fecha_fin: fechaFin });
    setCurrentDateRange({ start: fechaInicio, end: fechaFin });
    setPage(0);
    setCitas([]);
    setHasMore(true);
    fetchCitas(true, fechaInicio, fechaFin, selectedSucursal, selectedEstadoCita, selectedCategoria, nombreCliente, nombrePaciente);
  }, [selectedSucursal, selectedEstadoCita, selectedCategoria, nombreCliente, nombrePaciente]);

  // ─── Helper compartido: parsea el event.start de FullCalendar forzando zona El Salvador ───
  const parsearFechaEvento = (eventStart, debugLabel) => {
    const browserTZ = dayjs.tz.guess();
    const browserOffset = new Date().getTimezoneOffset() / 60;
    console.log('************************************************');
    console.log(`*** DEBUG: INICIO ${debugLabel} ***`);
    console.log(`DEBUG: 1. Zona Horaria del Navegador (Offset de UTC): ${browserTZ} (UTC${browserOffset > 0 ? '+' : '-'}${Math.abs(browserOffset)})`);
    console.log('DEBUG: 2. FullCalendar event.start (Raw Date toString):', eventStart.toString());
    console.log('DEBUG: 3. FullCalendar event.start (Raw Date toISOString):', eventStart.toISOString());

    // SOLUCIÓN ROBUSTA: Interpretación Forzada de Naive Date
    // 4. Extraer los componentes de la fecha y hora de la vista del calendario
    const dateString = `${eventStart.getFullYear()}-${String(eventStart.getMonth() + 1).padStart(2, '0')}-${String(eventStart.getDate()).padStart(2, '0')}T${String(eventStart.getHours()).padStart(2, '0')}:${String(eventStart.getMinutes()).padStart(2, '0')}:${String(eventStart.getSeconds()).padStart(2, '0')}`;

    // 5. Forzar a Day.js a *interpretar* esta cadena de tiempo como si fuera de El Salvador (UTC-6).
    const nuevaFecha = dayjs.tz(dateString, 'YYYY-MM-DDTHH:mm:ss', 'America/El_Salvador');

    // 6. Convertir el instante de tiempo correcto a la cadena ISO UTC para el backend.
    const nuevaFechaFormatted = nuevaFecha.toISOString();

    console.log('DEBUG: 4. Cadena de Tiempo Naive Generada:', dateString);
    console.log('DEBUG: 5. Instante en San Salvador (TZ):', nuevaFecha.format('YYYY-MM-DD HH:mm:ss Z'));
    console.log('DEBUG: 6. Instante Final ISO UTC (Backend):', nuevaFechaFormatted);
    console.log('************************************************');

    return { nuevaFecha, nuevaFechaFormatted };
  };

  // ─── Helper compartido: actualiza el rango visible y recarga citas ───
  const actualizarRangoYRecargar = (nuevaFechaFormatted) => {
    const nuevaFechaObj = dayjs(nuevaFechaFormatted);
    const startRange = dayjs(currentDateRange.start);
    const endRange = dayjs(currentDateRange.end);
    if (nuevaFechaObj.isBefore(startRange) || nuevaFechaObj.isAfter(endRange)) {
      const fechaInicio = nuevaFechaObj.startOf('day').format('YYYY-MM-DD HH:mm:ss');
      const fechaFin = nuevaFechaObj.endOf('day').format('YYYY-MM-DD HH:mm:ss');
      console.log('Nueva fecha fuera del rango, actualizando rango:', { fecha_inicio: fechaInicio, fecha_fin: fechaFin });
      setCurrentDateRange({ start: fechaInicio, end: fechaFin });
      fetchCitas(true, fechaInicio, fechaFin, selectedSucursal, selectedEstadoCita, selectedCategoria, nombreCliente, nombrePaciente);
    } else {
      fetchCitas(true, currentDateRange.start, currentDateRange.end, selectedSucursal, selectedEstadoCita, selectedCategoria, nombreCliente, nombrePaciente);
    }
  };

  const handleEventDrop = async (info) => {
    const citaId = info.event.id;
    const { nuevaFecha, nuevaFechaFormatted } = parsearFechaEvento(info.event.start, 'handleEventDrop (Mover Cita)');

    console.log('Evento arrastrado - Payload final:', {
      id_cita: citaId,
      nueva_fecha_utc_enviada: nuevaFechaFormatted,
    });

    try {
      console.log('Enviando actualización al backend:', { id_cita: citaId, fecha_cita: nuevaFechaFormatted });
      const response = await api.put(`/citas/update-duracion/${citaId}`, { fecha_cita: nuevaFechaFormatted });
      console.log('Cita actualizada correctamente en el backend:', response.data);

      const calendarApi = calendarRef.current.getApi();
      const event = calendarApi.getEventById(citaId);
      if (event) {
        event.setStart(nuevaFecha.toDate()); // Objeto Date en UTC
        const duracionProgramada = response.data.duracion || 30;
        event.setEnd(nuevaFecha.add(duracionProgramada, 'minute').toDate()); // Objeto Date en UTC
        event.setExtendedProp('duracion', duracionProgramada);
        console.log('Evento actualizado en FullCalendar:', {
          id: citaId,
          nueva_fecha: nuevaFechaFormatted,
          duracion: duracionProgramada,
          start: event.start.toISOString(),
          end: event.end.toISOString()
        });
      }

      actualizarRangoYRecargar(nuevaFechaFormatted);
      setSnackbarMessage('Cita actualizada correctamente');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error al actualizar la cita:', err);
      setError('No se pudo actualizar la cita. Intenta de nuevo.');
      setSnackbarMessage('Error al actualizar la cita');
      setSnackbarOpen(true);
      info.revert();
    }
  };

  const handleEventResize = async (info) => {
    const citaId = info.event.id;
    const nuevaDuracionEnMinutos = Math.round((info.event.end.getTime() - info.event.start.getTime()) / 60000);
    const { nuevaFecha, nuevaFechaFormatted } = parsearFechaEvento(info.event.start, 'handleEventResize (Redimensionar Cita)');

    console.log('Evento redimensionado - Payload final:', {
      id_cita: citaId,
      nueva_fecha_utc_enviada: nuevaFechaFormatted,
      nueva_duracion: nuevaDuracionEnMinutos
    });

    try {
      console.log('Enviando actualización de cita y duración:', { id_cita: citaId, fecha_cita: nuevaFechaFormatted, duracion: nuevaDuracionEnMinutos });
      const response = await api.put(`/citas/update-duracion/${citaId}`, { fecha_cita: nuevaFechaFormatted, duracion: nuevaDuracionEnMinutos });
      console.log('Cita actualizada correctamente en el backend:', response.data);

      const calendarApi = calendarRef.current.getApi();
      const event = calendarApi.getEventById(citaId);
      if (event) {
        event.setStart(nuevaFecha.toDate()); // Objeto Date en UTC
        event.setEnd(nuevaFecha.add(nuevaDuracionEnMinutos, 'minute').toDate()); // Objeto Date en UTC
        event.setExtendedProp('duracion', nuevaDuracionEnMinutos);
        console.log('Evento actualizado en FullCalendar:', {
          id: citaId,
          nueva_fecha: nuevaFechaFormatted,
          duracion: nuevaDuracionEnMinutos,
          start: event.start.toISOString(),
          end: event.end.toISOString()
        });
      }

      actualizarRangoYRecargar(nuevaFechaFormatted);
      setSnackbarMessage('Cita actualizada correctamente');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error al actualizar la cita:', err);
      setError('No se pudo actualizar la cita. Intenta de nuevo.');
      setSnackbarMessage('Error al actualizar la cita');
      setSnackbarOpen(true);
      info.revert();
    }
  };

  const handleLoadMore = () => {
    console.log('Cargando más citas para el rango actual:', currentDateRange);
    if (currentDateRange.start && currentDateRange.end) {
      fetchCitas(false, currentDateRange.start, currentDateRange.end, selectedSucursal, selectedEstadoCita, selectedCategoria, nombreCliente, nombrePaciente);
    }
  };

  const handleEventClick = (clickInfo) => {
    setSelectedCita(clickInfo.event);
    setNewEstadoCita(clickInfo.event.extendedProps.estado_cita || '');
    setDialogOpen(true);
  };

  const handleDateClick = (info) => {
    setSelectedDate(info.dateStr);
    setProgramarModalOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCita(null);
    setNewEstadoCita('');
  };

  const handleCloseProgramarModal = () => {
    setProgramarModalOpen(false);
    setSelectedDate(null);
    if (currentDateRange.start && currentDateRange.end) {
      fetchCitas(true, currentDateRange.start, currentDateRange.end, selectedSucursal, selectedEstadoCita, selectedCategoria, nombreCliente, nombrePaciente);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = () => {
    if (!filters.nombreCliente && !filters.nombrePaciente && filters.selectedSucursal === '' && filters.selectedEstadoCita === '' && filters.selectedCategoria === '') {
      setError('Debe seleccionar al menos un filtro o proporcionar un nombre para buscar.');
      setSnackbarMessage('Debe seleccionar al menos un filtro o proporcionar un nombre para buscar.');
      setSnackbarOpen(true);
      return;
    }
    console.log('Iniciando búsqueda con filtros:', filters);
    setPage(0);
    setCitas([]);
    setHasMore(true);
    setFiltersDrawerOpen(false);
    if (currentDateRange.start && currentDateRange.end) {
      fetchCitas(
        true, 
        currentDateRange.start, 
        currentDateRange.end, 
        filters.selectedSucursal, 
        filters.selectedEstadoCita, 
        filters.selectedCategoria, 
        filters.nombreCliente, 
        filters.nombrePaciente
      );
    }
  };

  const handleResetSearch = () => {
    console.log('Reseteando filtros y búsqueda');
    setFilters(initialFilters);
    setPage(0);
    setCitas([]);
    setHasMore(true);
    setError(null);
    if (currentDateRange.start && currentDateRange.end) {
      fetchCitas(true, currentDateRange.start, currentDateRange.end, '', '', '', '', '');
    }
    setFiltersDrawerOpen(false);
  };

  const handleUpdateEstadoCita = async () => {
    if (!selectedCita || !newEstadoCita) return;
    try {
      console.log('Actualizando estado de cita:', { id_cita: selectedCita.id, estado_cita: newEstadoCita });
      const response = await api.put(`/citas/update-estado/${selectedCita.id}`, { estado_cita: newEstadoCita });
      console.log('Estado de cita actualizado en el backend:', response.data);
      
      const calendarApi = calendarRef.current.getApi();
      const event = calendarApi.getEventById(selectedCita.id);
      if (event) {
        event.setExtendedProp('estado_cita', newEstadoCita);
        event.setProp('backgroundColor', getEventColor(newEstadoCita));
      }

      setSelectedCita(prev => ({
        ...prev,
        extendedProps: { ...prev.extendedProps, estado_cita: newEstadoCita }
      }));
      
      setSnackbarMessage('Estado de cita actualizado correctamente');
      setSnackbarOpen(true);
      fetchCitas(true, currentDateRange.start, currentDateRange.end, selectedSucursal, selectedEstadoCita, selectedCategoria, nombreCliente, nombrePaciente);
    } catch (err) {
      console.error('Error al actualizar estado de cita:', err);
      setError('No se pudo actualizar el estado de la cita.');
      setSnackbarMessage('Error al actualizar el estado de la cita');
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage('');
  };

  useEffect(() => {
    const fetchPacienteDatos = async () => {
      if (idPaciente) {
        try {
          const response = await api.get(`/pacientes/${idPaciente}`);
          const pacienteData = response.data;
          let nombreRaza = 'N/A';
          if (pacienteData.id_raza) {
            const razaResponse = await api.get(`/razas/${pacienteData.id_raza}`);
            nombreRaza = razaResponse.data.nombre_raza;
          }
          setFilters(prev => ({ ...prev, nombrePaciente: pacienteData.nombre_paciente, nombreRaza }));
        } catch (err) {
          console.error('Error al obtener datos del paciente o raza:', err);
        }
      }
    };
    fetchPacienteDatos();
  }, [idPaciente]);

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="xl" sx={{ pt: 10 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4">
              Calendario de Citas {idPaciente ? `para ${filters.nombrePaciente || 'Cargando...'} (${filters.nombreRaza || 'Cargando...'})` : ''} 
            </Typography>
          </Box>
          
          {!idPaciente && (
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setFiltersDrawerOpen(true)}
            >
              Filtros
            </Button>
          )}
        </Box>

        {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek,addEventButton',
          }}
          customButtons={{
            addEventButton: {
              text: 'Programar Cita',
              click: () => setProgramarModalOpen(true),
            },
          }}
          buttonText={{
            today: 'Hoy',
            month: 'Mes',
            week: 'Semana',
            day: 'Día',
            list: 'Lista',
          }}
          events={citas}
          eventClick={handleEventClick}
          dateClick={handleDateClick}
          datesSet={handleDatesSet}
          editable={true}
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
          eventResizableFromStart={true}
          locale="es"
          height="auto"
          eventTimeFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
            timeZone: 'America/El_Salvador',
          }}
          slotLabelFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
            timeZone: 'America/El_Salvador',
          }}
          timeZone='America/El_Salvador'
        />

        {hasMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button onClick={handleLoadMore} variant="contained" disabled={loading}>
              Cargar más citas
            </Button>
          </Box>
        )}

        <FiltrosCalendario
          open={filtersDrawerOpen}
          onClose={() => setFiltersDrawerOpen(false)}
          filters={filters}
          onFilterChange={handleFilterChange}
          onSearch={handleSearch}
          onResetSearch={handleResetSearch}
          sucursales={sucursales}
          estadosCita={estadosCita}
          categorias={categorias}
          loading={loading}
        />

            <Dialog  open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth PaperProps={{sx: { borderRadius: 3 }  }}
    >
      {/* Cabecera con color según estado */}
      <DialogTitle sx={{
        bgcolor: selectedCita ? getEventColor(selectedCita.extendedProps.estado_cita || 'PEN') : '#757575',
        color: 'white',
        py: 3,
      }}>
        <Typography variant="h5" fontWeight="bold">
          Detalles de la Cita
        </Typography>
        {selectedCita && (
          <Typography variant="subtitle1" sx={{ opacity: 0.9, mt: 0.5 }}>
            {estadosCita.find(e => e.value === selectedCita.extendedProps.estado_cita)?.label || 'Pendiente'}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers sx={{ py: 4 }}>
        {selectedCita && (
          <TableContainer>
            <Table size="medium">

              {/* === PACIENTE Y PROPIETARIO === */}
              <TableBody sx={{ bgcolor: 'grey.100', '& td': { borderBottom: 'none' } }}>
                <TableRow>
                  <TableCell width={220} sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                    Paciente
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                    {selectedCita.extendedProps.nombre_paciente}
                  </TableCell>
                </TableRow>
                <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                      Propietario
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          cursor: 'pointer',
                          color: 'primary.main',
                          fontWeight: '500',
                          '&:hover': { textDecoration: 'underline' },
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                        onClick={() => window.open(`/clientes/${selectedCita.extendedProps.id_cliente}`, '_blank')}
                      >
                        {selectedCita.extendedProps.nombre_cliente}
                        <OpenInNewIcon sx={{ fontSize: 14, opacity: 0.7 }} />
                      </Typography>
                    </TableCell>
                  </TableRow>
              </TableBody>

              {/* === FECHA Y HORA DESTACADA === */}
              <TableBody>
                <TableRow sx={{ bgcolor: 'primary.50' }}>
                  <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    Fecha y Hora
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.2rem', color: 'primary.main' }}>
                    {dayjs(cleanDateString(selectedCita.extendedProps.fecha_cita || selectedCita.start))
                      .tz('America/El_Salvador')
                      .format('dddd, D [de] MMMM [del] YYYY [a las] hh:mm A')}
                  </TableCell>
                </TableRow>
              </TableBody>

              {/* === CONTACTO === */}
              <TableHead>
                <TableRow>
                  <TableCell colSpan={2} sx={{ bgcolor: 'grey.100', fontWeight: 'bold', py: 1.5 }}>
                    Información de Contacto
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary' }}>Teléfonos</TableCell>
                  <TableCell>{selectedCita.extendedProps.telefonos_cliente || '—'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary' }}>Email</TableCell>
                  <TableCell sx={{ color: 'primary.main' }}>
                    {selectedCita.extendedProps.email_cliente || '—'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary' }}>Dirección</TableCell>
                  <TableCell>{selectedCita.extendedProps.direccion_cliente || '—'}</TableCell>
                </TableRow>
              </TableBody>

              {/* === DETALLES DE LA CITA === */}
              <TableHead>
                <TableRow>
                  <TableCell colSpan={2} sx={{ bgcolor: 'grey.100', fontWeight: 'bold', py: 1.5 }}>
                    Detalles de la Consulta
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary', width: 220 }}>Razón de consulta</TableCell>
                  <TableCell>{selectedCita.extendedProps.razon_consulta || 'No especificada'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary' }}>Peso actual</TableCell>
                  <TableCell>
                    {selectedCita.extendedProps.peso_paciente 
                      ? `${selectedCita.extendedProps.peso_paciente} kg` 
                      : '—'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary', verticalAlign: 'top' }}>Descripción / Notas</TableCell>
                  <TableCell>
                    <Box sx={{
                      bgcolor: 'grey.100',
                      p: 2,
                      borderRadius: 2,
                      minHeight: 70,
                      whiteSpace: 'pre-wrap'
                    }}>
                      {selectedCita.extendedProps.descripcion || 'Sin notas adicionales'}
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>

              {/* === CAMBIO DE ESTADO === */}
              <TableHead>
                <TableRow>
                  <TableCell colSpan={2} sx={{ bgcolor: 'grey.100', fontWeight: 'bold', py: 1.5 }}>
                    Cambiar Estado de la Cita
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary' }}>Estado actual</TableCell>
                  <TableCell>
                    <Select
                      fullWidth
                      value={newEstadoCita}
                      onChange={(e) => setNewEstadoCita(e.target.value)}
                      sx={{ bgcolor: 'background.paper' }}
                    >
                      {estadosCita.map((estado) => (
                        <MenuItem key={estado.value} value={estado.value}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 14,
                                height: 14,
                                borderRadius: '50%',
                                bgcolor: getEventColor(estado.value),
                              }}
                            />
                            {estado.label}
                            </Box>
                          </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                </TableRow>
              </TableBody>

              {/* === INFO TÉCNICA (pequeña) === */}
              <TableBody sx={{ '& td': { fontSize: '0.8rem', color: 'text.secondary' } }}>
                <TableRow>
                  <TableCell colSpan={2} sx={{ textAlign: 'center', py: 1 }}>
                    ID Cita: {selectedCita.id} • Sucursal: {selectedCita.extendedProps.id_sucursal} • Paciente ID: {selectedCita.extendedProps.id_paciente}
                  </TableCell>
                </TableRow>
              </TableBody>

            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: 'grey.50', gap: 2 }}>
        <Button onClick={handleCloseDialog} size="large">
          Cerrar
        </Button>
        <Button
          onClick={handleUpdateEstadoCita}
          variant="contained"
          size="large"
          disabled={!newEstadoCita || newEstadoCita === selectedCita?.extendedProps.estado_cita}
          sx={{ minWidth: 180 }}
        
        >
          Actualizar Estado
        </Button>
      </DialogActions>
    </Dialog>

        <Dialog open={programarModalOpen} onClose={handleCloseProgramarModal} fullWidth maxWidth="sm">
          <DialogTitle>Programar Nueva Cita</DialogTitle>
          <DialogContent>
            <ProgramarConsulta visible={true} onClose={handleCloseProgramarModal} initialDate={selectedDate} />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseProgramarModal}>Cerrar</Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={1500}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ zIndex: 1500 }}
        >
          <Alert onClose={handleSnackbarClose} severity={error ? 'error' : 'success'} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default CitasCalendario;